import React, { createContext, useContext, useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { useAuth } from '../context/AuthContext';

const PostHogContext = createContext(null);

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

export const PostHogProvider = ({ children }) => {
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [consent, setConsent] = useState(() => localStorage.getItem('manteo_cookie_consent'));

  const initPostHog = () => {
    try {
      const storedConsent = localStorage.getItem('manteo_cookie_consent');

      if (storedConsent === 'true' && POSTHOG_KEY) {
        if (!posthog.__loaded) {
          posthog.init(POSTHOG_KEY, {
            api_host: POSTHOG_HOST,
            person_profiles: 'identified_only',
            defaults: '2026-01-30',
            autocapture: true,
            disable_session_recording: true,
            capture_pageview: true,
          });
        } else {
          posthog.opt_in_capturing();
        }
        setIsInitialized(true);
      } else if (storedConsent === 'false' && posthog.__loaded) {
        posthog.opt_out_capturing();
        setIsInitialized(false);
      }
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
      setIsInitialized(false);
    }
  };

  useEffect(() => {
    initPostHog();

    const handleConsentChange = () => {
      setConsent(localStorage.getItem('manteo_cookie_consent'));
      initPostHog();
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);
    return () => window.removeEventListener('cookieConsentChanged', handleConsentChange);
  }, []);

  useEffect(() => {
    if (isInitialized && user) {
      posthog.identify(user.id);
    }
  }, [isInitialized, user]);

  const optOut = () => {
    localStorage.setItem('manteo_cookie_consent', 'false');
    if (posthog.__loaded) {
      posthog.opt_out_capturing();
    }
    setIsInitialized(false);
    setConsent('false');
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  const optIn = () => {
    localStorage.setItem('manteo_cookie_consent', 'true');
    setConsent('true');
    initPostHog();
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  return (
    <PostHogContext.Provider value={{ isInitialized, consent, optOut, optIn }}>
      {children}
    </PostHogContext.Provider>
  );
};

export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (!context) throw new Error('usePostHog must be used within a PostHogProvider');
  return context;
};
